import React, { ReactNode, useLayoutEffect, useRef } from 'react'
import { Avatar, Button, ProgressBar } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { useMediaQuery } from 'shared/hooks/use-match-media'
import { getFullOffsetOfElement } from 'shared/utils/get-full-offset-of-element'
import { useCourseData } from 'modules/course/hooks/use-course-data'
import { useCourseMenu } from 'modules/course/hooks/use-course-menu'
import useStepRouter from 'modules/course/hooks/use-step-router'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'
import { CourseModuleList } from './course-module-list'
import { CourseSideMenuSkeleton } from './course-side-menu-skeleton'

const MIN_MAX_MODULE_HEIGHT = 200
const BOTTOM_MODULE_OFFSET = 35

export interface CourseSideMenuProps {
  className?: string
  isLoading?: boolean
  menuClassName?: string
}

export const CourseSideMenu = ({
  className,
  isLoading,
  menuClassName,
}: React.PropsWithChildren<CourseSideMenuProps>): ReactNode => {
  const menuContainerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const moduleListRef = useRef<HTMLUListElement>(null)

  const isLG = useMediaQuery('(min-width: 1024px)', false)

  const { t } = useTranslation()

  const { onNextStep, onPreviousStep, isStart, isEnd } = useStepRouter()

  const { courseData } = useCourseData()
  const { courseMenu } = useCourseMenu({ courseId: courseData?.id })

  useLayoutEffect(() => {
    const html = document.querySelector('html')
    const resizeSideMenu = (): void => {
      if (menuRef.current && moduleListRef.current && menuContainerRef.current) {
        const menuFullOffset = getFullOffsetOfElement(menuRef.current)
        const moduleListFullOffset = getFullOffsetOfElement(moduleListRef.current)
        const htmlScrollTop = html?.scrollTop || 0

        const height = `${Math.max(
          document.body.clientHeight -
            (moduleListFullOffset - menuFullOffset) -
            Math.max(menuFullOffset - htmlScrollTop, 0) -
            BOTTOM_MODULE_OFFSET,
          MIN_MAX_MODULE_HEIGHT,
        )}px`
        moduleListRef.current.style.maxHeight = height
        if (moduleListRef.current.clientHeight === MIN_MAX_MODULE_HEIGHT && isLG) {
          menuRef.current.style.overflowY = 'auto'
        } else {
          menuRef.current.style.overflowY = 'hidden'
        }
        if (menuContainerRef.current.clientHeight >= document.body.clientHeight - 40 && isLG) {
          menuRef.current.style.height = 'calc(100vh - 40px)'
        } else {
          menuRef.current.style.height = 'unset'
        }
      }
    }
    resizeSideMenu()
    window.addEventListener('scroll', resizeSideMenu)
    window.addEventListener('resize', resizeSideMenu)
    return () => {
      window.removeEventListener('scroll', resizeSideMenu)
      window.removeEventListener('resize', resizeSideMenu)
    }
  }, [courseMenu, isLG])

  const isActuallyLoading = isLoading === undefined ? !courseData && !courseMenu : isLoading

  if (isActuallyLoading) return <CourseSideMenuSkeleton />

  return (
    <div
      ref={menuRef}
      className={twMerge(
        `top-5 customScroll w-full rounded-md bg-THEME-block-background lg:sticky lg:w-82.5`,
        className,
        addPublicCustomClassNamePrefix('course__side-menu'),
      )}
    >
      <div
        ref={menuContainerRef}
        className={twMerge('p-6', menuClassName)}
        data-testid="course-side-menu-content"
      >
        <div className="flex justify-center">
          <Avatar
            alt={courseData?.instructorName || ''}
            displayName={courseData?.instructorName || ''}
            rootProps={{
              className: twMerge(
                'size-20',
                addPublicCustomClassNamePrefix('course__instruction_photo'),
              ),
            }}
            size="xl"
            src={courseData?.instructorPhoto?.path || '/default_post_author_avatar.png'}
          />
        </div>
        <p
          className={twMerge(
            `mt-5 text-center text-2xl font-bold text-THEME-primary`,
            addPublicCustomClassNamePrefix('course__instruction_name'),
          )}
          data-test-id="course-author-name"
        >
          {courseData?.instructorName || ''}
        </p>
        <div className="flex gap-3 pt-7">
          <Button
            variant="secondary"
            className={twMerge(
              `basis-6/12 border-THEME-primary! bg-THEME-block-background! font-bold text-THEME-primary! hover:border-THEME-block-background! hover:bg-THEME-primary! hover:text-THEME-block-background! disabled:border-THEME-disabled! disabled:text-THEME-disabled!`,
              addPublicCustomClassNamePrefix([
                'course__button',
                'course__button--secondary',
                'course__button--previous-lecture',
              ]),
            )}
            disabled={isStart}
            onClick={onPreviousStep}
            data-test-id="course-previous-button"
          >
            {t('course.previous_step_button_label')}
          </Button>
          <Button
            variant="primary"
            className={twMerge(
              `basis-6/12 border-THEME-secondary! bg-THEME-secondary! font-bold text-THEME-block-background! hover:bg-THEME-hover-secondary! hover:text-THEME-block-background! disabled:border-THEME-disabled! disabled:bg-THEME-disabled!`,
              addPublicCustomClassNamePrefix([
                'course__button',
                'course__button--primary',
                'course__button--next-lecture',
              ]),
            )}
            disabled={isEnd}
            onClick={onNextStep}
            data-test-id="course-next-button"
          >
            {t('course.next_step_button_label')}
          </Button>
        </div>
        <ProgressBar
          value={courseData?.progress || 0}
          rootProps={{
            'data-test-id': 'course-progress-bar',
            className: twMerge(
              'mt-8 w-full text-THEME-secondary!',
              addPublicCustomClassNamePrefix('course__progress-bar'),
            ),
          }}
          indicatorProps={{ className: 'bg-THEME-secondary!' }}
        />
        <CourseModuleList ref={moduleListRef} />
      </div>
    </div>
  )
}
